/**
 * Geo-localized symptom landing pages — high commercial intent.
 * Route pattern: /{slug-panne-ville}
 *
 * These are distinct from /panne/{slug} (generic). Each entry targets a
 * highly competitive query combining a symptom + a city / zone.
 */

export const PANNES_GEO = [
    {
        slug: "lave-linge-qui-fuit-paris",
        appliance: "lave-linge",
        applianceLabel: "Lave-linge",
        applianceServiceSlug: "reparateur-lave-linge-paris",
        zoneLabel: "Paris",
        zoneIntro: "20 arrondissements",
        h1: "Lave-linge qui fuit à Paris — Intervention sous 24 h",
        title: "Lave-linge qui fuit Paris : réparation 24 h, devis | Electrocase",
        description: "Lave-linge qui fuit à Paris ? Diagnostic à domicile sous 24 h. Joint, durite, pompe, bac : devis ferme, garantie 18 mois. ☎ 01 84 80 46 86.",
        keywords: "lave-linge fuite paris, réparateur lave-linge fuit paris, fuite machine à laver paris",
        icon: "fa-solid fa-droplet",
        urgence: "élevée",
        coutEstime: "95 € – 240 €",
        delai: "24 h",
        intro: "Une fuite de lave-linge dans un appartement parisien = risque immédiat de dégât des eaux pour le voisin du dessous. Coupez l'eau, posez une serpillière, et appelez : nous intervenons sous 24 h dans les 20 arrondissements de Paris.",
        relatedPanne: "lave-linge-qui-fuit",
        cityHubLink: "/reparateur-electromenager-paris",
        localContext: "Paris concentre 60 % des sinistres dégât des eaux d'Île-de-France selon la FFA. Dans les immeubles haussmanniens, la fuite d'un appareil mal entretenu peut traverser 2 à 3 étages en moins d'une heure. Notre délai d'intervention prioritaire à Paris : moins de 24 h, intervention le jour même selon les disponibilités.",
        causes: [
            { t: "Joint de hublot abîmé", d: "1ère cause à Paris (eau dure ≈27 °f → encrassement plus rapide). 95-150 €." },
            { t: "Durite percée", d: "Calcaire qui fragilise les durites. 110-160 €." },
            { t: "Pompe de vidange fissurée", d: "Pose comprise 130-200 €." },
            { t: "Bac à produits engorgé", d: "Très fréquent avec lessive liquide. 50-70 €." },
        ],
        diy: [
            "Coupez immédiatement le robinet d'arrivée d'eau",
            "Photographiez la fuite (origine et étendue) pour le diagnostic à distance",
            "Si voisinage : prévenez vos voisins du dessous (gain de temps en cas de sinistre)",
            "Notez la marque + modèle (souvent à l'intérieur de la porte du hublot)",
        ],
    },
    {
        slug: "frigo-ne-refroidit-plus-paris",
        appliance: "frigo",
        applianceLabel: "Frigo",
        applianceServiceSlug: "reparateur-frigo-paris",
        zoneLabel: "Paris",
        zoneIntro: "20 arrondissements",
        h1: "Frigo qui ne refroidit plus à Paris — Urgence 24 h",
        title: "Frigo ne refroidit plus Paris : réparation urgente | Electrocase",
        description: "Frigo en panne à Paris ? Intervention urgente sous 24 h, diagnostic compresseur, thermostat, ventilateur. Devis gratuit, garantie 18 mois. ☎ 01 84 80 46 86.",
        keywords: "frigo en panne paris, frigo ne refroidit plus paris, réparation réfrigérateur paris urgent",
        icon: "fa-solid fa-snowflake",
        urgence: "très élevée",
        coutEstime: "120 € – 380 €",
        delai: "24 h",
        intro: "Un frigo en panne à Paris, c'est en moyenne 80 € de courses gâchées en moins de 24 h. Intervention prioritaire le jour même selon disponibilité dans les 20 arrondissements.",
        relatedPanne: "frigo-ne-refroidit-plus",
        cityHubLink: "/reparateur-electromenager-paris",
        localContext: "À Paris, les cuisines exiguës limitent souvent l'aération des frigos encastrés. Cette mauvaise ventilation est la 1ère cause de panne précoce du compresseur que nous observons dans la capitale. Diagnostic incluant systématiquement le contrôle d'aération.",
        causes: [
            { t: "Condenseur encrassé (cuisines mal ventilées)", d: "Très fréquent à Paris. Nettoyage 70-90 €." },
            { t: "Ventilateur intérieur bloqué (No-Frost)", d: "Givre qui bloque la pâle. 130-180 €." },
            { t: "Thermostat / sonde HS", d: "110-160 €." },
            { t: "Compresseur fin de vie", d: "Sur appareils > 10 ans. 280-380 € — à arbitrer." },
            { t: "Fuite de gaz réfrigérant", d: "Recharge + soudure 220-300 € (habilitation requise)." },
        ],
        diy: [
            "Sauvez les produits les plus précieux chez un voisin",
            "Aspirez la grille noire à l'arrière du frigo (dépoussiérage)",
            "Vérifiez que le frigo n'est pas collé au mur (5 cm minimum)",
            "Mesurez la température réelle avec un thermomètre",
        ],
    },
    {
        slug: "lave-vaisselle-bouche-paris",
        appliance: "lave-vaisselle",
        applianceLabel: "Lave-vaisselle",
        applianceServiceSlug: "reparateur-lave-vaisselle-paris",
        zoneLabel: "Paris",
        zoneIntro: "20 arrondissements",
        h1: "Lave-vaisselle bouché à Paris — Débouchage à domicile",
        title: "Lave-vaisselle bouché Paris : pompe, filtre, code E15 | Electrocase",
        description: "Lave-vaisselle bouché ou code E15 à Paris ? Diagnostic à domicile sous 24 h. Filtre, pompe, durite : devis ferme, garantie 18 mois. ☎ 01 84 80 46 86.",
        keywords: "lave-vaisselle bouché paris, lave-vaisselle ne vidange plus paris, code E15 paris",
        icon: "fa-solid fa-arrows-up-down",
        urgence: "élevée",
        coutEstime: "80 € – 220 €",
        delai: "24-48 h",
        intro: "Eau stagnante au fond, code E15 affiché, ou pompe qui rugit sans évacuer ? Dans 60 % des cas, c'est un filtre bouché (réparable rapidement). Intervention à domicile dans tout Paris.",
        relatedPanne: "lave-vaisselle-ne-vidange-plus",
        cityHubLink: "/reparateur-electromenager-paris",
        localContext: "À Paris, l'eau dure (TH ≈ 27 °f) accélère l'encrassement des bras de lavage et le calcaire dans la pompe. Nos techniciens parisiens emportent systématiquement les pièces les plus couramment changées (pompe vidange, capteur niveau, sécurité Aquastop).",
        causes: [
            { t: "Filtre central encrassé", d: "60 % des cas. Nettoyage 50-70 €." },
            { t: "Pompe de vidange bloquée (verre, os)", d: "120-170 €." },
            { t: "Durite de vidange entartrée", d: "Spécificité parisienne. 90-130 €." },
            { t: "Code E15 (eau dans le socle)", d: "Fuite interne à localiser. 150-220 €." },
        ],
        diy: [
            "Démonter le filtre central, rincer eau chaude",
            "Vérifier le siphon de l'évier (cause souvent oubliée)",
            "Lancer un cycle vide vinaigre blanc (détartrage)",
            "Si E15 affiché : NE PAS redémarrer, attendre le technicien",
        ],
    },
    {
        slug: "four-ne-chauffe-plus-paris",
        appliance: "four",
        applianceLabel: "Four",
        applianceServiceSlug: "reparation-four-paris",
        zoneLabel: "Paris",
        zoneIntro: "20 arrondissements",
        h1: "Four qui ne chauffe plus à Paris — Réparation à domicile",
        title: "Four ne chauffe plus Paris : résistance, thermostat | Electrocase",
        description: "Four qui ne chauffe plus à Paris ? Diagnostic résistance, thermostat, sélecteur à domicile. Intervention 24-48 h, devis gratuit. ☎ 01 84 80 46 86.",
        keywords: "four ne chauffe plus paris, résistance four paris, réparation four paris",
        icon: "fa-solid fa-fire",
        urgence: "faible",
        coutEstime: "100 € – 220 €",
        delai: "24-48 h",
        intro: "Four qui n'atteint plus 200° ou plats qui ne cuisent pas ? Résistance grillée, thermostat HS, sélecteur défectueux : diagnostic à domicile dans tout Paris.",
        relatedPanne: "four-ne-chauffe-plus",
        cityHubLink: "/reparateur-electromenager-paris",
        localContext: "À Paris, beaucoup de fours encastrés haut de gamme (Miele, Bosch série 8, Siemens iQ700, Smeg) équipent les cuisines de standing. Nos techniciens parisiens sont formés aux spécificités de démontage des façades encastrées (+30-50 € main-d'œuvre).",
        causes: [
            { t: "Résistance voûte grillée", d: "Pièce 50-100 € + pose. Total 130-180 €." },
            { t: "Résistance sole grillée", d: "Idem. 140-190 €." },
            { t: "Résistance chaleur tournante", d: "150-200 €." },
            { t: "Thermostat HS", d: "Pièce 40 € + pose. Total 120-150 €." },
            { t: "Sélecteur de fonctions", d: "140-200 €." },
        ],
        diy: [
            "Tester chaque mode séparément (voûte, sole, gril, chaleur tournante)",
            "Vérifier le disjoncteur dédié",
            "Inspecter visuellement les résistances (filament coupé visible)",
            "Notez la marque + modèle (étiquette au-dessous ou sur côté du four)",
        ],
    },
    {
        slug: "seche-linge-en-panne-hauts-de-seine",
        appliance: "seche-linge",
        applianceLabel: "Sèche-linge",
        applianceServiceSlug: "reparation-seche-linge-paris",
        zoneLabel: "Hauts-de-Seine",
        zoneIntro: "26 communes du 92",
        h1: "Sèche-linge en panne dans les Hauts-de-Seine",
        title: "Sèche-linge en panne 92 : pompe à chaleur, résistance | Electrocase",
        description: "Sèche-linge qui ne chauffe plus ou bac plein dans le 92 ? Diagnostic PAC, condensation, évacuation à domicile sous 24-48 h. Devis gratuit. ☎ 01 84 80 46 86.",
        keywords: "sèche-linge en panne 92, réparation sèche-linge hauts-de-seine, sèche-linge ne chauffe plus 92",
        icon: "fa-solid fa-fan",
        urgence: "moyenne",
        coutEstime: "120 € – 250 €",
        delai: "24-48 h",
        intro: "Sèche-linge qui ne chauffe plus, voyant bac plein affiché, ou pompe à chaleur en panne ? Intervention à domicile dans les 26 communes des Hauts-de-Seine.",
        relatedPanne: "seche-linge-ne-chauffe-plus",
        cityHubLink: "/reparateur-electromenager-hauts-de-seine",
        localContext: "Le 92 concentre beaucoup de pavillons et grandes copropriétés (Neuilly, Levallois, Boulogne) équipés de sèche-linges récents à pompe à chaleur (PAC). 70 % des pannes que nous diagnostiquons sur PAC sont liées à l'évaporateur encrassé — réparable simplement par nettoyage.",
        causes: [
            { t: "Filtre à condensation encrassé (PAC)", d: "Nettoyage 60-90 €." },
            { t: "Évaporateur PAC encrassé", d: "Cause #1 sur PAC. 100-150 €." },
            { t: "Résistance grillée (évacuation/condensation classique)", d: "150-200 €." },
            { t: "Klixon thermique déclenché", d: "Sécurité. 100-130 €." },
            { t: "Pompe à chaleur HS", d: "Diagnostic spécifique. 200-280 €." },
        ],
        diy: [
            "Nettoyer le filtre à peluches après CHAQUE cycle",
            "Sur PAC : nettoyer l'évaporateur (cache amovible accessible)",
            "Vider le bac inférieur même s'il semble vide",
            "Vérifier la grille d'aération arrière",
        ],
    },
    {
        slug: "plaque-induction-ne-fonctionne-plus-paris",
        appliance: "four",
        applianceLabel: "Plaque induction",
        applianceServiceSlug: "reparation-four-paris",
        zoneLabel: "Paris",
        zoneIntro: "20 arrondissements",
        h1: "Plaque induction qui ne fonctionne plus à Paris",
        title: "Plaque induction en panne Paris : foyer HS, code erreur | Electrocase",
        description: "Plaque induction qui ne s'allume plus ou foyer HS à Paris ? Diagnostic vitrocéramique, bobine, carte de puissance à domicile. ☎ 01 84 80 46 86.",
        keywords: "plaque induction en panne paris, foyer induction ne fonctionne plus, plaque vitrocéramique paris",
        icon: "fa-solid fa-bolt",
        urgence: "moyenne",
        coutEstime: "140 € – 320 €",
        delai: "48 h",
        intro: "Foyer qui clignote, plaque qui ne reconnaît plus la casserole, code erreur affiché ? Nos techniciens parisiens diagnostiquent à domicile, sans démontage hasardeux.",
        relatedPanne: "four-ne-chauffe-plus",
        cityHubLink: "/reparateur-electromenager-paris",
        localContext: "Les cuisines parisiennes haut de gamme intègrent beaucoup de plaques induction Miele, Bosch, Siemens, De Dietrich, Smeg. Diagnostic complet à domicile, intervention sécurisée (haute tension).",
        causes: [
            { t: "Bobine d'induction défectueuse", d: "Un foyer ne chauffe plus. 180-260 €." },
            { t: "Carte de puissance HS", d: "Plaque qui ne s'allume plus du tout. 220-320 €." },
            { t: "Capteur de détection casserole HS", d: "Ne reconnaît plus le récipient. 140-200 €." },
            { t: "Câble d'alimentation HS", d: "Raccordement triphasé/monophasé à vérifier. 120-160 €." },
        ],
        diy: [
            "Tester avec une autre casserole compatible induction (test aimant)",
            "Vérifier le disjoncteur dédié (souvent 32A)",
            "Notez le code erreur exact affiché (Exx, Fxx)",
            "Vérifier que la plaque est de niveau",
        ],
    },
    {
        slug: "lave-linge-qui-fuit-neuilly-sur-seine",
        appliance: "lave-linge",
        applianceLabel: "Lave-linge",
        applianceServiceSlug: "reparateur-lave-linge-paris",
        zoneLabel: "Neuilly-sur-Seine",
        zoneIntro: "92200",
        h1: "Lave-linge qui fuit à Neuilly-sur-Seine — Intervention 24 h",
        title: "Lave-linge qui fuit Neuilly : réparation à domicile | Electrocase",
        description: "Lave-linge qui fuit à Neuilly-sur-Seine ? Diagnostic à domicile sous 24 h. Joint, durite, pompe : devis ferme, garantie 18 mois. ☎ 01 84 80 46 86.",
        keywords: "lave-linge fuite neuilly, réparation lave-linge neuilly, fuite machine à laver 92200",
        icon: "fa-solid fa-droplet",
        urgence: "élevée",
        coutEstime: "95 € – 260 €",
        delai: "24 h",
        intro: "Neuilly-sur-Seine concentre une forte proportion d'appareils haut de gamme Miele et Bosch série 8. Notre équipe spécialisée Miele Service Partner intervient sous 24 h.",
        relatedPanne: "lave-linge-qui-fuit",
        cityHubLink: "/reparateur-electromenager-neuilly-sur-seine",
        localContext: "Neuilly = 98 interventions Miele en 2025, premier marché 92 pour le premium. Eau particulièrement dure (≈30 °f) qui accélère l'usure des joints et durites. Disponibilité pièces Miele sous 24-72 h.",
        causes: [
            { t: "Joint de hublot abîmé (eau dure)", d: "Très fréquent à Neuilly. 110-180 € (joints Miele/Bosch premium)." },
            { t: "Durite percée par calcaire", d: "120-170 €." },
            { t: "Pompe de vidange fissurée", d: "140-220 €." },
            { t: "Bac à produits engorgé", d: "60-90 €." },
        ],
        diy: [
            "Coupez immédiatement le robinet d'arrivée d'eau (souvent derrière la machine)",
            "Photographiez la fuite + notez le modèle Miele/Bosch précis",
            "Pour Miele : vérifiez la lampe d'erreur (codes F11, F36, F39)",
            "Si appartement avec voisin dessous : prévenez par mesure de précaution",
        ],
    },
    {
        slug: "frigo-en-panne-boulogne-billancourt",
        appliance: "frigo",
        applianceLabel: "Frigo",
        applianceServiceSlug: "reparateur-frigo-paris",
        zoneLabel: "Boulogne-Billancourt",
        zoneIntro: "92100",
        h1: "Frigo en panne à Boulogne-Billancourt — Urgence 24 h",
        title: "Frigo en panne Boulogne-Billancourt – 24 h | Electrocase",
        description: "Frigo qui ne refroidit plus à Boulogne-Billancourt ? Diagnostic à domicile sous 24 h, devis ferme, garantie 18 mois. ☎ 01 84 80 46 86.",
        keywords: "frigo en panne boulogne, réfrigérateur ne refroidit plus boulogne-billancourt, réparation frigo 92100",
        icon: "fa-solid fa-snowflake",
        urgence: "très élevée",
        coutEstime: "120 € – 380 €",
        delai: "24 h",
        intro: "Boulogne-Billancourt est notre 2ème zone d'intervention dans le 92 (après Neuilly). Frigo qui ne refroidit plus ? Nous intervenons sous 24 h, parfois le jour même.",
        relatedPanne: "frigo-ne-refroidit-plus",
        cityHubLink: "/reparateur-electromenager-boulogne-billancourt",
        localContext: "Boulogne-Billancourt = mix d'appartements modernes ZAC Seguin (Liebherr, Bosch encastrés) et d'immeubles 1980-2000 (Whirlpool, Brandt). Notre stock dédié 92 couvre les 5 marques les plus rencontrées localement.",
        causes: [
            { t: "Condenseur encrassé (encastrement)", d: "Très fréquent à Boulogne. Nettoyage 70-100 €." },
            { t: "Ventilateur intérieur bloqué", d: "Frigo No-Frost. 130-180 €." },
            { t: "Thermostat / sonde HS", d: "110-160 €." },
            { t: "Compresseur fin de vie", d: "Sur appareils > 10 ans. 280-380 €." },
            { t: "Fuite gaz réfrigérant (Liebherr)", d: "Recharge + soudure 240-340 €." },
        ],
        diy: [
            "Sauvez les produits essentiels (congélateur d'un voisin)",
            "Mesurez la température réelle (thermomètre)",
            "Aspirez la grille de ventilation",
            "Vérifiez la fermeture du joint de porte (feuille de papier)",
        ],
    },
];

export const findPanneGeo = (slug) => PANNES_GEO.find((p) => p.slug === slug);
